import type { DateType } from '@date-io/type'
import * as React from 'react'

import { useUtils } from '../../_shared/hooks/useUtils'
import type { MaterialUiPickersDate } from '../../typings/date'

import Year from './Year'

export interface YearSelectionProps {
  date: MaterialUiPickersDate
  minDate: DateType
  maxDate: DateType
  onChange: (date: MaterialUiPickersDate, isFinish: boolean) => void
  disablePast?: boolean | null | undefined
  disableFuture?: boolean | null | undefined
  animateYearScrolling?: boolean | null | undefined
  onYearChange?: (date: MaterialUiPickersDate) => void
}

export const YearSelection: React.FunctionComponent<YearSelectionProps> = ({
  date,
  onChange,
  onYearChange,
  minDate,
  maxDate,
  disablePast,
  disableFuture,
  animateYearScrolling,
}) => {
  const utils = useUtils()
  const selectedYearRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (selectedYearRef.current && selectedYearRef.current.scrollIntoView) {
      try {
        selectedYearRef.current.scrollIntoView({
          block: 'center',
          behavior: animateYearScrolling ? 'smooth' : 'auto',
        })
      } catch (e) {
        // call without arguments in case when scrollIntoView works improperly (e.g. Firefox 52-57)
        selectedYearRef.current.scrollIntoView()
      }
    }
  }, []) // eslint-disable-line

  const currentYear = utils.getYear(date)
  const onYearSelect = React.useCallback(
    (year: number) => {
      const newDate = utils.setYear(date, year)
      if (onYearChange) {
        onYearChange(newDate)
      }

      onChange(newDate, true)
    },
    [date, onChange, onYearChange, utils],
  )

  return (
    <div className={'MuiPickersYearSelection-container'}>
      {utils.getYearRange(minDate, maxDate).map((year) => {
        const yearNumber = utils.getYear(year)
        const selected = yearNumber === currentYear

        return (
          <Year
            key={utils.getYearText(year)}
            selected={selected}
            value={yearNumber}
            onSelect={onYearSelect}
            ref={selected ? selectedYearRef : undefined}
            disabled={Boolean(
              (disablePast && utils.isBeforeYear(year, utils.date())) ||
              (disableFuture && utils.isAfterYear(year, utils.date())),
            )}
          >
            {utils.getYearText(year)}
          </Year>
        )
      })}
    </div>
  )
}
